import { AuthenticationResult } from '@azure/msal-browser';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CredentialResponse } from '@react-oauth/google';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router';

import { BASE } from 'common/src/routes/BaseRoutes';

import usePageTitle from 'portal/src/navigation/usePageTitle';
import FormController from 'portal/src/components/common/forms/FormController';
import { getEmailField } from 'portal/src/components/common/forms/commonFormFields';

import useAuth from '../../auth/useAuth';
import MicrosoftLoginButton from '../../components/auth/MicrosoftLoginButton';
import GoogleLoginButton from '../../components/auth/GoogleLoginButton';
import ImageLoader from '../../components/common/ImageLoader';

const logoPng = import.meta.env.VITE_LOGO_LIGHT || 'logo.blue.png';

const passwordField = {
  name: 'password',
  label: 'Password',
  placeholder: 'Password',
  required: true,
  type: 'password',
};

export default function Login() {
  usePageTitle('Login');

  const {
    googleLogin,
    initiateEnterpriseSSOLogin,
    isAuthenticated,
    isPasswordExpired,
    login,
    microsoftLogin,
    tokenLogin,
  } = useAuth();
  const { control, handleSubmit, trigger } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [email] = useWatch({ name: ['email'], control });
  const [error, setError] = useState<boolean>(false);
  const [externalError, setExternalError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [params] = useSearchParams();
  const location = useLocation();
  const code = params.get('code');
  const ssoError = params.get('error') === 'sso';

  let from = location.state?.from?.pathname;
  let fromSearch = location.state?.from?.search || '';

  if (!from || from === BASE.AUTH.LOGIN) {
    from = '/';
    fromSearch = '';
  }

  const redirectPath = from + fromSearch;

  const emailField = {
    ...getEmailField(true),
    onChange: () => setShowPassword(false),
  };

  const handleContinue = async () => {
    if (!email) {
      return;
    }

    const isEmailValid = await trigger('email');
    if (!isEmailValid) {
      return;
    }

    setLoading(true);
    setError(false);

    const authorizationUrl = await initiateEnterpriseSSOLogin(email);
    if (authorizationUrl) {
      window.location.href = authorizationUrl;
    } else {
      setShowPassword(true);
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (payload: any) => {
    setLoading(true);

    if (!(await login(payload.email, payload.password))) {
      setError(true);
    }
    setLoading(false);
  };

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    setLoading(true);

    if (!(await googleLogin(credentialResponse.credential))) {
      setExternalError(true);
    }
    setLoading(false);
  };

  const handleMicrosoftLogin = async (
    authenicationResult: AuthenticationResult,
  ) => {
    setLoading(true);

    if (!(await microsoftLogin(authenicationResult.accessToken))) {
      setExternalError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    const handleTokenLogin = async () => {
      setLoading(true);

      if (!(await tokenLogin(code))) {
        setError(true);
      }
      setLoading(false);
    };

    if (code) {
      handleTokenLogin();
    }
  }, [code, tokenLogin]);

  // Cleaning all query cache data
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticated()) {
    if (isPasswordExpired()) {
      return <Navigate to={BASE.AUTH.CHANGE_EXPIRED_PASSWORD} replace />;
    }
    return <Navigate to={redirectPath} replace />;
  }

  const submit = handleSubmit(handlePasswordLogin);
  const showContinue = !showPassword && !!email;

  // Disable implicit form submission; Enter clicks whichever button is shown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') {
      return;
    }

    e.preventDefault();

    if (showPassword) {
      submit();
    } else if (showContinue) {
      handleContinue();
    }
  };

  return (
    <>
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'linear-gradient(135deg, rgba(191,210,237,1) 0%, rgba(34,56,84,1) 60%, rgba(14,36,60,1) 80%, rgba(14,36,60,1) 100%);',
          width: '100wh',
          height: '100vh',
        }}
      >
        <Paper
          elevation={6}
          sx={{ padding: '0 2rem 2rem', maxWidth: '28rem', width: '28rem' }}
        >
          <Stack spacing={2} sx={{ marginTop: 4, alignItems: 'center' }}>
            <ImageLoader
              src={logoPng}
              alt="logo"
              width={240}
              effect="opacity"
              effectDuration={1000}
              wrapperStyle={{ transitionDelay: '1s' }}
            />

            <Typography
              variant="h5"
              sx={{ textAlign: 'center', paddingTop: 1 }}
            >
              Log in to your account
            </Typography>
            <Typography variant="body2">
              Don&apos;t have an account?{' '}
              <MuiLink
                href="https://nimble-energy.com/contact-us/"
                underline="none"
                target="_blank"
                rel="noreferer"
              >
                Contact us to start
              </MuiLink>
            </Typography>
            <GoogleLoginButton
              onSuccess={handleGoogleLogin}
              onError={() => setError(true)}
            />
            <MicrosoftLoginButton
              onSuccess={handleMicrosoftLogin}
              onError={() => setError(true)}
            />
            <Box sx={{ width: '24rem' }}>
              <Divider
                sx={{
                  mt: 2,
                }}
              >
                Or with email and password
              </Divider>
            </Box>
            <form
              autoComplete="off"
              noValidate
              onKeyDown={handleKeyDown}
              onSubmit={(e) => e.preventDefault()}
            >
              <Stack spacing={2} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: '24rem' }}>
                  <FormController control={control} fieldConf={emailField} />
                </Box>
                {showContinue && (
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={handleContinue}
                  >
                    Continue
                  </Button>
                )}
                {showPassword && (
                  <>
                    <Box sx={{ width: '24rem' }}>
                      <FormController
                        control={control}
                        fieldConf={passwordField}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={submit}
                    >
                      Login
                    </Button>
                  </>
                )}
                <Typography sx={{ textAlign: 'center', color: 'error.main' }}>
                  {ssoError &&
                    'Single sign-on failed. Please try again or contact your administrator.'}
                  {!ssoError && (error || externalError) && 'Unable to login. '}
                  {!ssoError &&
                    error &&
                    !externalError &&
                    'Please verify that your email and password are correct.'}
                  {!ssoError &&
                    !error &&
                    externalError &&
                    'We do not recognize that account.'}
                </Typography>
              </Stack>
            </form>
            <Link
              to={BASE.AUTH.FORGOT_PASSWORD}
              style={{ color: 'inherit', textDecoration: 'inherit' }}
            >
              <Typography variant="body2">Forgot password?</Typography>
            </Link>
            <Typography variant="caption">
              By continuing you agree to our{' '}
              <MuiLink
                href="https://privacy.nimble-energy.com/terms-of-service"
                target="_blank"
                rel="noreferer"
              >
                Terms of Service
              </MuiLink>{' '}
              and{' '}
              <MuiLink
                href="https://privacy.nimble-energy.com/"
                target="_blank"
                rel="noreferer"
              >
                Privacy Policy
              </MuiLink>
            </Typography>
          </Stack>
        </Paper>
      </Stack>
      <Backdrop open={loading}>
        <CircularProgress aria-label="Logging in" />
      </Backdrop>
    </>
  );
}
